import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// Module-level cache: Supabase stores the session locally, so getSession() is
// synchronous after the first call. We pre-fire it as soon as this module is
// imported (before any component mounts) to minimize the loading window.
let _cachedSession = undefined; // undefined = not yet resolved
const _sessionPromise = supabase.auth.getSession().then(({ data: { session } }) => {
  _cachedSession = session ?? null;
  return _cachedSession;
}).catch(() => {
  _cachedSession = null;
  return null;
});

export function useAuth() {
  // If the session resolved before first render, start with it — no loading spinner.
  const [user, setUser] = useState(() => _cachedSession?.user ?? null);
  const [session, setSession] = useState(() => _cachedSession ?? null);
  const [loading, setLoading] = useState(_cachedSession === undefined);

  useEffect(() => {
    // If still pending (module just imported), wait for the pre-fired promise
    if (_cachedSession === undefined) {
      _sessionPromise.then((session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      });
    }

    // Fallback timeout in case Supabase hangs
    const timeout = setTimeout(() => setLoading(false), 3000);

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => { subscription.unsubscribe(); clearTimeout(timeout); };
  }, []);

  async function signIn({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Ensure user_settings exists for accounts created with email confirmation
    // (those accounts skip the signUp insert since there's no session at that point)
    if (data.user) {
      const { data: existing } = await supabase
        .from('user_settings')
        .select('user_id')
        .eq('user_id', data.user.id)
        .maybeSingle();
      if (!existing) {
        await supabase.from('user_settings').insert({
          user_id: data.user.id,
          business_name: data.user.user_metadata?.business_name ?? '',
          trade_type: data.user.user_metadata?.trade_type ?? '',
          default_markup: 20,
          default_tax_rate: 8.5,
          payment_terms: 'Net 30',
        });
      }
    }
    return data;
  }

  async function signUp({ email, password, fullName, businessName, tradeType }) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          business_name: businessName,
          trade_type: tradeType,
        },
      },
    });
    if (error) throw error;

    // Create initial user_settings record
    if (data.user) {
      await supabase.from('user_settings').insert({
        user_id: data.user.id,
        business_name: businessName,
        trade_type: tradeType,
        default_markup: 20,
        default_tax_rate: 8.5,
        payment_terms: 'Net 30',
      });
    }

    return data;
  }

  async function signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    // Force-clear state as a safety net in case onAuthStateChange doesn't fire
    setSession(null);
    setUser(null);
  }

  return {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  };
}
